import React from 'react';
import { useTheme } from '@emotion/react';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  type FormControlLabelProps,
  type RadioGroupProps,
  type RadioProps,
} from '@mui/material';
import _ from 'lodash';
import { type ExperienceMultiSurveyCardReasonSection } from '@bbkAdminRedux/rtkq/experience.slice';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import { type NextStepHandler } from '@bbkFunnel/events';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  ReasonOtherText,
  type ReasonOtherHandler,
} from '@bbkFunnelComponents/survey/reasons/reasons-other-text';
import {
  type SurveyMultiRowResponse,
  type SurveyMultiRowResponseHandler,
} from '@bbkFunnelComponents/survey-multi-row';
import {
  isMultiOtherInputEnabled,
  reasonShowsTextInput,
} from '@bbkFunnelUtils/reason-other';

export type ReasonOtherTextClearHandler = (cb: () => void) => void;

type Props = {
  items: ExperienceMultiSurveyCardReasonSection['options'];
  response: SurveyMultiRowResponse;
  handler: SurveyMultiRowResponseHandler;
  tierValue: string;
  nextStep: NextStepHandler;
  continue: () => void;
  question: ExperienceMultiSurveyCardReasonSection;
  handleReasonOtherText?: ReasonOtherHandler;
  clearReasonOtherText?: ReasonOtherTextClearHandler;
  error?: string;
};
export const ReasonsContent: ReactFCC<Props> = ({
  response,
  question,
  handleReasonOtherText,
  items,
  error,
  clearReasonOtherText,
  nextStep,
  continue: continueFn,
  handler,
  tierValue,
}) => {
  const theme = useTheme() as EmotionTheme;
  const enableMultiOtherInput = isMultiOtherInputEnabled(items);

  const showsTextInput = (reasonValue: string) =>
    reasonShowsTextInput(reasonValue, items, enableMultiOtherInput);

  const ErrorDisplay = () => (
    <div
      css={{
        color: theme.errorVal,
        fontSize: 12,
        paddingTop: 5,
      }}
    >
      {error}
    </div>
  );

  const selectedValue = response.reason_for_leaving.find(
    (x) => x.name === question.name
  );

  const ChangeFn: RadioGroupProps['onChange'] = (e, value) => {
    const item = items.find((item) => item.value === value);
    const action = _.get(item, 'action');
    const previousValue = selectedValue?.value;

    handler(e, value, item?.tier ?? 'tier0', tierValue);

    const doWork = () => {
      nextStep(action);
      continueFn();
    };
    // A reason wired to an action (e.g. an offer) always advances so the shopper sees the offer
    // first; if they want to leave feedback they can do so after dismissing it. Text input still
    // renders inline for reasons without an action (e.g. Other).
    const shouldNavigate = action !== 'open_text_input' && !!action;

    const finishChange = () => {
      if (shouldNavigate) {
        doWork();
      }
    };

    if (previousValue !== value && clearReasonOtherText) {
      clearReasonOtherText(finishChange);
    } else {
      finishChange();
    }
  };

  const selectedShowsTextInput =
    !!selectedValue && showsTextInput(selectedValue.value);

  const reasonOtherTextVal =
    selectedShowsTextInput && response.reason_other !== undefined
      ? response.reason_other
      : '';

  const radioColor = error ? theme.errorVal : theme.textColor;
  const radioColorChecked = error ? theme.errorVal : theme.primaryColor;

  const WrappedLabel = (props: FormControlLabelProps) => (
    <FormControlLabel
      {...props}
      sx={{
        margin: 0,
        '& .MuiFormControlLabel-label': {
          color: radioColor,
          fontFamily: theme.fontFamily,
        },
      }}
    />
  );

  const WrappedRadio = (props: RadioProps) => (
    <Radio
      {...props}
      sx={{
        color: radioColor,
        margin: '0 0 0 -10px',
        opacity: 0.4,
        '&.Mui-checked': {
          color: radioColorChecked,
          opacity: 1,
        },
      }}
    />
  );

  return (
    <div>
      <RadioGroup
        name={question.name}
        value={selectedValue?.value ?? ''}
        onChange={ChangeFn}
      >
        {items.map((item) => {
          const isSelected = selectedValue?.value === item.value;
          const showInput = isSelected && showsTextInput(item.value);

          return (
            <React.Fragment key={item.value}>
              <WrappedLabel
                control={<WrappedRadio />}
                value={item.value}
                label={item.label}
              />
              {showInput && handleReasonOtherText && (
                <ReasonOtherText
                  showOther
                  reasonOtherTextVal={reasonOtherTextVal}
                  handleOtherText={handleReasonOtherText}
                  tierValue={tierValue}
                  question={question}
                  selectedItem={selectedValue}
                />
              )}
            </React.Fragment>
          );
        })}
      </RadioGroup>
      {error && <ErrorDisplay />}
    </div>
  );
};
